import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { styled, palette, Text, Stack, withUtility, spacing, useUtility } from '@hub-la/design-system';
import { AssetType } from '@hub-la/fe-asset';
import { useContainer } from '@hub-la/fe-container';
import { Analytics } from '@hub-la/fe-core-analytics';
import { Thumbnail } from '@hub-la/fe-thumbnail';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Accordion as Accordion$1, alpha, AccordionSummary, AccordionDetails } from '@mui/material';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Events } from '../../../domain/dtos/events.js';
import { GetReleaseTime } from '../../../usecases/get-release-time.js';
import { useRealtimeSections } from '../../hooks/use-realtime-sections.js';
import '@hub-la/fe-core-http-client';
import 'react-query';
import '../../../usecases/save-file.js';
import '../../../domain/dtos/query-key.js';
import '../../../usecases/save-post.js';
import { Duration } from './duration.js';
import { PostProgress } from './post-progress.js';
import { SectionProgress } from './section-progress.js';

let _ = t => t,
  _t,
  _t2,
  _t3;
const Accordion = styled(Accordion$1)(_t || (_t = _`
  border: none;
  border-bottom: 1px solid ${0};
  background-color: ${0};

  & .MuiAccordionSummary-root,
  & .MuiAccordionDetails-root,
  & .MuiAccordionSummary-content {
    padding: 0;
    margin: 0;
  }

  &:not(:last-child) {
    border-bottom: 0;
  }

  &:before {
    display: none;
  }

  *::-webkit-scrollbar {
    display: none;
  }
`), palette('surfaceVariant'), palette('surface'));
const TitleWithClamp = styled(Text)(_t2 || (_t2 = _`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`));
const Card = styled(Stack, {
  shouldForwardProp: prop => prop !== 'isActive'
})(_t3 || (_t3 = _`
  cursor: pointer;
  border-radius: 4px;
  background-color: ${0};
  margin-bottom: ${0};
  flex-direction: row;
  border: 2px solid
    ${0};

  :hover {
    border: 2px solid
      ${0};
  }
`), withUtility(({
  palette
}) => alpha(palette('onSurface'), 0.1)), spacing(4), withUtility(({
  palette
}, {
  isActive
}) => isActive ? palette('onSurfaceVariant') : 'transparent'), withUtility(({
  palette
}, {
  isActive
}) => isActive ? palette('onSurfaceVariant') : palette('onSurfaceVariant')));
const SidebarContent = ({
  postId,
  productId,
  defaultSection
}) => {
  const history = useHistory();
  const {
    palette
  } = useUtility();
  const container = useContainer();
  const analytics = container.get(Analytics);
  const {
    sections,
    realtimePostProgress
  } = useRealtimeSections();
  const [openSections, setOpenSections] = useState(new Set([defaultSection]));
  const [cachedSectionsThumbnail, setCachedSectionsThumbnail] = useState(new Set([defaultSection]));
  const openSection = element => {
    setOpenSections(prevState => new Set([...prevState, element]));
    // Cache the section that has been opened to avoid download the thumbnails again or download all thumbnails at once when the page is loaded
    setCachedSectionsThumbnail(prevState => new Set([...prevState, element]));
  };
  const closeSection = element => {
    setOpenSections(prevState => {
      const newState = new Set([...prevState]);
      newState.delete(element);
      return newState;
    });
  };
  useEffect(() => {
    setOpenSections(new Set([defaultSection]));
  }, [defaultSection]);
  const handleClick = (sectionId, postId) => {
    const postLink = `/post/${postId}`;
    analytics.track(Events.POST_DETAIL.SIDEBAR_CONTENT.POST_CLICKED, {
      productId,
      sectionId,
      postId
    });
    window.scrollTo(0, 0);
    history.push(postLink);
  };
  const handleChange = panel => () => {
    analytics.track(Events.POST_DETAIL.SIDEBAR_CONTENT.SECTION_CLICKED, {
      productId,
      sectionId: panel
    });
    openSections.has(panel) ? closeSection(panel) : openSection(panel);
  };
  return jsx(Fragment, {
    children: sections.map(section => {
      var _a, _b, _c;
      const releaseAt = new Date((_a = section.releaseAt) !== null && _a !== void 0 ? _a : 0);
      // Check if the user has already opened the section to avoid download the thumbnails again
      const isSectionThumbnailCached = cachedSectionsThumbnail.has(section.id);
      return jsxs(Accordion, Object.assign({
        disableGutters: true,
        elevation: 0,
        square: true,
        onChange: handleChange(section.id),
        expanded: openSections.has(section.id),
        style: {
          borderBottom: `1px solid ${palette('surfaceVariant')}`
        },
        sx: {
          px: 6,
          pb: openSections.has(section.id) ? 6 : 0
        }
      }, {
        children: [jsx(AccordionSummary, Object.assign({
          expandIcon: jsx(ExpandMoreOutlinedIcon, {
            sx: {
              fontSize: 22,
              color: palette('onSurface')
            }
          })
        }, {
          children: jsxs(Stack, Object.assign({
            direction: "column",
            spacing: 2,
            width: "100%",
            my: 6
          }, {
            children: [jsxs(Stack, Object.assign({
              direction: "row",
              width: "100%",
              alignContent: "center",
              justifyContent: "space-between"
            }, {
              children: [jsxs(Stack, Object.assign({
                direction: "row",
                spacing: 2,
                alignItems: "center"
              }, {
                children: [releaseAt > new Date() && jsx(LockOutlinedIcon, {
                  style: {
                    color: palette('outline')
                  }
                }), jsx(TitleWithClamp, Object.assign({
                  variant: "h4",
                  fontWeight: 500,
                  color: "onSurface"
                }, {
                  children: section.name
                }))]
              })), ((_b = section === null || section === void 0 ? void 0 : section.posts) === null || _b === void 0 ? void 0 : _b.length) > 0 && jsx(SectionProgress, {
                count: (_c = section === null || section === void 0 ? void 0 : section.posts) === null || _c === void 0 ? void 0 : _c.length,
                progress: section === null || section === void 0 ? void 0 : section.progress
              })]
            })), releaseAt > new Date() && jsx(Text, Object.assign({
              variant: "caption",
              color: "outline"
            }, {
              children: new GetReleaseTime().execute(releaseAt)
            }))]
          }))
        })), jsx(AccordionDetails, Object.assign({
          sx: {
            overflow: 'scroll'
          }
        }, {
          children: section.posts.map(post => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const releaseAt = new Date((_a = post.releaseAt) !== null && _a !== void 0 ? _a : 0);
            return jsxs(Card, Object.assign({
              onClick: () => handleClick(section.id, post.id),
              isActive: post.id === postId,
              position: "relative"
            }, {
              children: [isSectionThumbnailCached && jsx(Thumbnail, {
                width: 69,
                height: 122,
                asset: (_b = post === null || post === void 0 ? void 0 : post.customCover) !== null && _b !== void 0 ? _b : post === null || post === void 0 ? void 0 : post.cover,
                lockContent: releaseAt > new Date(),
                hasProgressBar: false,
                resizeWidth: 420
              }), jsxs(Stack, Object.assign({
                justifyContent: "center",
                marginLeft: 4
              }, {
                children: [jsx(TitleWithClamp, Object.assign({
                  variant: "body2",
                  color: "onSurface",
                  fontWeight: 700
                }, {
                  children: post.title
                })), releaseAt > new Date() ? jsxs(Stack, Object.assign({
                  direction: "row",
                  spacing: 1,
                  alignItems: "center",
                  marginTop: 2
                }, {
                  children: [jsx(LockOutlinedIcon, {
                    fontSize: "small",
                    style: {
                      color: palette('outline')
                    }
                  }), jsx(Text, Object.assign({
                    variant: "caption",
                    color: "outline"
                  }, {
                    children: new GetReleaseTime().execute(releaseAt)
                  }))]
                })) : jsx(Duration, {
                  post: post
                })]
              })), jsx(PostProgress, {
                progress: postId === post.id ? realtimePostProgress : ((_d = (_c = post === null || post === void 0 ? void 0 : post.cover) === null || _c === void 0 ? void 0 : _c.progress) === null || _d === void 0 ? void 0 : _d.currentProgress) || 0,
                maxProgress: (_g = (_f = (_e = post === null || post === void 0 ? void 0 : post.cover) === null || _e === void 0 ? void 0 : _e.metadata) === null || _f === void 0 ? void 0 : _f.duration) !== null && _g !== void 0 ? _g : (_j = (_h = post === null || post === void 0 ? void 0 : post.cover) === null || _h === void 0 ? void 0 : _h.metadata) === null || _j === void 0 ? void 0 : _j.pages,
                viewed: !((_k = post === null || post === void 0 ? void 0 : post.cover) === null || _k === void 0 ? void 0 : _k.url) && (post === null || post === void 0 ? void 0 : post.viewed) || ((_l = post === null || post === void 0 ? void 0 : post.cover) === null || _l === void 0 ? void 0 : _l.type) === AssetType.image && (post === null || post === void 0 ? void 0 : post.viewed)
              })]
            }), post.id);
          })
        }))]
      }), section.id);
    })
  });
};

export { SidebarContent };
